import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import axios from 'axios';
import { User, Lock, Save, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettingsPage = () => {
  const { user, getAuthHeaders, logout } = useAuth();
  const [saving, setSaving] = useState(false);
  
  // Profile form
  const [profile, setProfile] = useState({
    name: '',
    email: ''
  });
  
  // Password form
  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        email: user.email || ''
      });
    }
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const response = await axios.put(`${API}/auth/profile`, profile, {
        headers: getAuthHeaders()
      });
      
      toast.success('Profil mis à jour ! Veuillez vous reconnecter si vous avez changé votre email.');
      
      // Si l'email a changé, déconnecter l'utilisateur
      if (profile.email !== user.email) {
        setTimeout(() => {
          logout();
        }, 2000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (passwords.new_password.length < 6) {
      toast.error('Le nouveau mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setSaving(true);
    
    try {
      await axios.put(`${API}/auth/password`, {
        current_password: passwords.current_password,
        new_password: passwords.new_password
      }, {
        headers: getAuthHeaders()
      });
      
      toast.success('Mot de passe modifié avec succès !');
      setPasswords({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-8 max-w-2xl">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl text-[#3D2E35]">Mon compte</h1>
          <p className="text-[#6B5A62] mt-1">Gérez vos informations personnelles et votre mot de passe</p>
        </div>

        {/* Profile Section */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#8B4B7C] flex items-center justify-center">
              <User className="text-white" size={20} />
            </div>
            <h2 className="font-heading text-xl text-[#3D2E35]">Informations du profil</h2>
          </div>
          
          <form onSubmit={saveProfile} className="space-y-5">
            <div className="space-y-2">
              <Label>Nom complet</Label>
              <Input
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                placeholder="Votre nom"
                required
                className="border-[#E8A5C4]/30"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Adresse email</Label>
              <Input
                type="email"
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                placeholder="votre@email.com"
                required
                className="border-[#E8A5C4]/30"
              />
              <p className="text-xs text-[#6B5A62]">
                Si vous changez votre email, vous devrez vous reconnecter
              </p>
            </div>
            
            <Button type="submit" disabled={saving} className="btn-tevaite text-white">
              <Save size={18} className="mr-2" />
              {saving ? 'Enregistrement...' : 'Enregistrer le profil'}
            </Button>
          </form>
        </div>

        {/* Password Section */}
        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#C45B9F] flex items-center justify-center">
              <Lock className="text-white" size={20} />
            </div>
            <h2 className="font-heading text-xl text-[#3D2E35]">Changer le mot de passe</h2>
          </div>
          
          <form onSubmit={changePassword} className="space-y-5">
            <div className="space-y-2">
              <Label>Mot de passe actuel</Label>
              <div className="relative">
                <Input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwords.current_password}
                  onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })}
                  placeholder="••••••••"
                  required
                  className="border-[#E8A5C4]/30 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B5A62] hover:text-[#8B4B7C]"
                >
                  {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Nouveau mot de passe</Label>
              <div className="relative">
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwords.new_password}
                  onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="border-[#E8A5C4]/30 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B5A62] hover:text-[#8B4B7C]"
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-xs text-[#6B5A62]">Minimum 6 caractères</p>
            </div>
            
            <div className="space-y-2">
              <Label>Confirmer le nouveau mot de passe</Label>
              <Input
                type="password"
                value={passwords.confirm_password}
                onChange={(e) => setPasswords({ ...passwords, confirm_password: e.target.value })}
                placeholder="••••••••"
                required
                className="border-[#E8A5C4]/30"
              />
            </div>
            
            <Button type="submit" disabled={saving} className="btn-tevaite text-white">
              <Lock size={18} className="mr-2" />
              {saving ? 'Modification...' : 'Changer le mot de passe'}
            </Button>
          </form>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettingsPage;
